import React from "react";
import { Link } from "react-router-dom";
import { Instagram } from "lucide-react";
import { Separator } from "./ui/separator";
import { Logo } from "./Logo";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { useLang } from "../i18n/LanguageContext";
import { BRAND } from "../lib/config";

export const Footer = () => {
  const { t } = useLang();
  const col = (title, links) => (
    <div>
      <h4 className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-4">{title}</h4>
      <ul className="space-y-2.5">
        {links.map((l) => (
          <li key={l.to}><Link to={l.to} className="text-sm text-foreground/70 hover:text-cyanx transition-colors">{l.label}</Link></li>
        ))}
      </ul>
    </div>
  );
  return (
    <footer className="relative mt-10 border-t border-border bg-[#070810]">
      <div className="absolute inset-0 pointer-events-none opacity-[0.06] noise-overlay" />
      <div className="container-lumeo py-16 relative">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-8">
          <div className="col-span-2">
            <Link to="/" className="flex items-center gap-2">
              <Logo />
            </Link>
            <p className="mt-4 text-sm text-muted-foreground max-w-xs">{t("footer.tagline")}</p>
            <div className="mt-5"><LanguageSwitcher testid="footer-language-switcher" /></div>
          </div>
          {col(t("footer.shop"), [
            { to: "/shop", label: t("nav.shop") },
            { to: "/bundles", label: t("nav.bundles") },
            { to: "/accessories", label: t("nav.accessories") },
            { to: "/compare", label: t("nav.compare") },
          ])}
          {col(t("footer.support"), [
            { to: "/advisor", label: t("nav.advisor") },
            { to: "/simulator", label: t("nav.simulator") },
            { to: "/shipping", label: t("footer.shipping") },
            { to: "/warranty", label: t("footer.warranty") },
          ])}
          {col(t("footer.company"), [
            { to: "/about", label: t("nav.about") },
            { to: "/contact", label: t("nav.contact") },
            { to: "/wholesale", label: t("footer.reseller") },
            { to: "/faq", label: t("nav.faq") },
          ])}
          {col(t("footer.legal"), [
            { to: "/privacy", label: t("footer.privacy") },
            { to: "/terms", label: t("footer.terms") },
          ])}
        </div>
        <Separator className="my-8 bg-border" />
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-muted-foreground">
          <p>© {new Date().getFullYear()} {BRAND}. {t("footer.rights")}</p>
          <div className="flex items-center gap-3">
            <p className="font-mono">Made for cinema · EU</p>
            <span className="hidden sm:inline text-border" aria-hidden="true">|</span>
            <span data-testid="footer-developer-credit" className="flex items-center gap-2">
              Developed by{" "}
              <a href="https://cloudservice.hr" target="_blank" rel="noopener noreferrer" data-testid="footer-cloudservice-link"
                className="text-foreground/70 hover:text-cyanx transition-colors underline-offset-4 hover:underline">cloudservice.hr</a>
              <a href="https://www.instagram.com/cloudservice.hr" target="_blank" rel="noopener noreferrer" aria-label="cloudservice.hr on Instagram" data-testid="footer-instagram-link"
                className="inline-flex h-6 w-6 items-center justify-center rounded-md hairline text-foreground/60 hover:text-cyanx hover:shadow-glow-cyan transition-all">
                <Instagram className="h-3.5 w-3.5" />
              </a>
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
};
